export type DocumentDateViewMode = 'jours' | 'mois' | 'annees';

export interface DocumentDateFilterState {
    viewMode: DocumentDateViewMode;
    dateFilterStart: string;
    dateFilterEnd: string;
    selectedDay: string;
    selectedMonthIndex: number | null;
    selectedYear: number | null;
    /**
     * Jour civil (YYYY-MM-DD) où ce filtre a été enregistré.
     * À chaque nouveau jour, la 1ʳᵉ ouverture repart sur « aujourd’hui ».
     */
    savedOnDay?: string;
}

const STORAGE_KEY_BY_TYPE = 'vente_document_date_filter_by_type';
/** Ancienne clé globale (migration). */
const STORAGE_KEY_LEGACY = 'vente_document_date_filter';

type DateFilterByTypeMap = Record<string, DocumentDateFilterState>;

function normalizeState(parsed: Partial<DocumentDateFilterState> | null | undefined): DocumentDateFilterState | null {
    if (!parsed?.dateFilterStart || !parsed?.dateFilterEnd) {
        return null;
    }
    return {
        viewMode: parsed.viewMode === 'mois' || parsed.viewMode === 'annees' ? parsed.viewMode : 'jours',
        dateFilterStart: String(parsed.dateFilterStart),
        dateFilterEnd: String(parsed.dateFilterEnd),
        selectedDay: String(parsed.selectedDay || parsed.dateFilterStart),
        selectedMonthIndex:
            parsed.selectedMonthIndex === null || parsed.selectedMonthIndex === undefined
                ? null
                : Number(parsed.selectedMonthIndex),
        selectedYear:
            parsed.selectedYear === null || parsed.selectedYear === undefined
                ? null
                : Number(parsed.selectedYear),
        savedOnDay: parsed.savedOnDay ? String(parsed.savedOnDay) : undefined,
    };
}

function readMap(): DateFilterByTypeMap {
    try {
        const raw = localStorage.getItem(STORAGE_KEY_BY_TYPE);
        if (!raw) {
            return {};
        }
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? (parsed as DateFilterByTypeMap) : {};
    } catch {
        return {};
    }
}

function writeMap(map: DateFilterByTypeMap): void {
    try {
        localStorage.setItem(STORAGE_KEY_BY_TYPE, JSON.stringify(map));
    } catch {
        /* quota / mode privé */
    }
}

function readLegacy(): DocumentDateFilterState | null {
    try {
        const raw = localStorage.getItem(STORAGE_KEY_LEGACY);
        if (!raw) {
            return null;
        }
        return normalizeState(JSON.parse(raw) as Partial<DocumentDateFilterState>);
    } catch {
        return null;
    }
}

/**
 * Charge le filtre date pour un type de document (ex. devis, facture, commande-client).
 * Chaque type a sa propre date mémorisée.
 */
export function loadDocumentDateFilter(documentType?: string | null): DocumentDateFilterState | null {
    const key = (documentType || '').trim();
    if (key) {
        const fromMap = normalizeState(readMap()[key]);
        if (fromMap) {
            return fromMap;
        }
        // Migration ponctuelle : ancienne valeur globale → ce type
        const legacy = readLegacy();
        if (legacy) {
            saveDocumentDateFilter(legacy, key);
            return legacy;
        }
        return null;
    }
    return readLegacy();
}

/**
 * Enregistre le filtre date pour un type de document.
 */
export function saveDocumentDateFilter(
    state: DocumentDateFilterState,
    documentType?: string | null
): void {
    const key = (documentType || '').trim();
    if (key) {
        const map = readMap();
        map[key] = state;
        writeMap(map);
        return;
    }
    try {
        localStorage.setItem(STORAGE_KEY_LEGACY, JSON.stringify(state));
    } catch {
        /* quota / mode privé */
    }
}
